import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import GarageIcon from '@mui/icons-material/Garage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { SectionHeader } from '../components/SectionHeader';
import { SectionLayout } from '../components/SectionLayout';
import { SectionNav, type SectionNavGroup } from '../components/SectionNav';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { GarageSection, garageAnchorId } from '../garage/GarageSection';
import { GarageFollowersDialog } from '../garage/GarageFollowersDialog';
import { GarageNameDialog } from '../garage/GarageNameDialog';
import { recordGarageVisit } from '../garage/garageVisits';
import type { Garage as GarageModel, GarageFollower, Paginated, SingleResponse } from '../api/types';

/** Sidebar entry that opens the new-garage dialog instead of scrolling. */
const NEW_GARAGE_KEY = 'new-garage';

/**
 * Every garage on one page — yours first, then the ones you follow — each in
 * its own block. The sidebar lists them as anchors; most-used first.
 */
export function Garage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [garages, setGarages] = useState<GarageModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState('');
  const [nameDialog, setNameDialog] = useState<{ garage: GarageModel | null } | null>(null);
  const [followersOf, setFollowersOf] = useState<GarageModel | null>(null);

  const load = useCallback(async () => {
    const [mine, followed] = await Promise.all([
      apiFetch<Paginated<GarageModel>>('/api/v1/garages?per_page=50'),
      apiFetch<{ data: GarageFollower[] }>('/api/v1/follows?filter[status]=approved'),
    ]);
    const ids = [
      ...mine.data.map((g) => g.id),
      ...followed.data.map((f) => f.garage_id),
    ];
    const details = await Promise.all(
      ids.map((id) => apiFetch<SingleResponse<GarageModel>>(`/api/v1/garages/${id}`))
    );
    setGarages(details.map((res) => res.data));
  }, []);

  useEffect(() => {
    if (!user) return;
    load()
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [user, load]);

  // Most-used first within each group; the API order breaks ties.
  const byUse = (a: GarageModel, b: GarageModel) => (b.my_visit_count ?? 0) - (a.my_visit_count ?? 0);
  const mine = useMemo(
    () => garages.filter((g) => g.user_id === user?.id).sort(byUse),
    [garages, user]
  );
  const followed = useMemo(
    () => garages.filter((g) => g.user_id !== user?.id).sort(byUse),
    [garages, user]
  );

  const navGroups: SectionNavGroup[] = [
    {
      key: 'mine',
      labelKey: 'garage.nav.mine',
      items: [
        ...mine.map((g) => ({
          key: garageAnchorId(g.id),
          label: g.name,
          icon: <GarageIcon fontSize="small" />,
        })),
        {
          key: NEW_GARAGE_KEY,
          labelKey: 'garage.actions.newGarage',
          icon: <AddIcon fontSize="small" />,
        },
      ],
    },
    ...(followed.length > 0
      ? [
          {
            key: 'followed',
            labelKey: 'garage.nav.followed',
            items: followed.map((g) => ({
              key: garageAnchorId(g.id),
              label: `${g.name} · ${g.user?.name ?? ''}`,
              icon: <VisibilityIcon fontSize="small" />,
            })),
          },
        ]
      : []),
  ];

  const jumpTo = (key: string) => {
    if (key === NEW_GARAGE_KEY) {
      setNameDialog({ garage: null });
      return;
    }
    setActiveKey(key);
    document.getElementById(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const garage = garages.find((g) => garageAnchorId(g.id) === key);
    if (garage) recordGarageVisit(garage.id);
  };

  const saveGarageName = async (name: string) => {
    const renaming = nameDialog?.garage;
    if (renaming) {
      await apiFetch(`/api/v1/garages/${renaming.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      });
    } else {
      await apiFetch('/api/v1/garages', { method: 'POST', body: JSON.stringify({ name }) });
    }
    await load();
    setNameDialog(null);
  };

  const deleteGarage = async (garage: GarageModel) => {
    if (!window.confirm(t('garage.deleteConfirm', { name: garage.name }))) return;
    setError('');
    try {
      await apiFetch(`/api/v1/garages/${garage.id}`, { method: 'DELETE' });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('garage.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader />
      <Stack spacing={3}>
        <SectionHeader
          title={t('garage.title')}
          description={t('garage.description')}
          action={
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="/cars/new">
              {t('garage.newCar')}
            </Button>
          }
        />

        {error && <Alert severity="error">{error}</Alert>}

        <SectionLayout
          mobileNavLabel={t('garage.nav.label')}
          sidebar={
            <SectionNav
              groups={navGroups}
              value={activeKey}
              onChange={jumpTo}
            />
          }
        >
          <Stack spacing={5} divider={<Divider flexItem />}>
            {[...mine, ...followed].map((garage) => (
              <GarageSection
                key={garage.id}
                garage={garage}
                isMine={garage.user_id === user?.id}
                onRename={() => setNameDialog({ garage })}
                onManageFollowers={() => setFollowersOf(garage)}
                onDelete={() => deleteGarage(garage)}
              />
            ))}
            {garages.length === 0 && (
              <Typography color="text.secondary">{t('garage.noCarsYet')}</Typography>
            )}
          </Stack>
        </SectionLayout>
      </Stack>

      <GarageNameDialog
        open={nameDialog !== null}
        garage={nameDialog?.garage ?? null}
        onClose={() => setNameDialog(null)}
        onSubmit={saveGarageName}
      />

      <GarageFollowersDialog
        garage={followersOf}
        onClose={() => setFollowersOf(null)}
        onAnswered={load}
      />
    </Container>
  );
}
